import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ChevronDown,
  User,
  Settings,
  LogOut,
  Loader2,
  Bell,
  LayoutGrid,
  ClipboardCheck,
  Users,
  UserPlus,
  Upload,
  MapPin,
  Building2,
  Wallet,
  ClipboardList,
  Banknote,
  History,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { RoutesLink } from "@/utility/RouteLinks";
import { logout as logoutAction } from "@/store/feature/UserSlice";
import { AuthService } from "@/services/Auth/Auth";
import { deleteCookies } from "@/utility/helper";
import { useAppDispatch, useAppSelector, type RootState } from "@/store/store";
import CommonModal from "@/components/common/CommonModal";

interface NavSubItem {
  path: string;
  label: string;
  icon: React.ElementType;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  children?: NavSubItem[];
}

const navItems: NavItem[] = [
  { path: RoutesLink.dashboard, label: "Overview", icon: LayoutGrid },
  { path:  RoutesLink.bulkAttendance, label: "Attendance", icon: ClipboardList },
  {
    path: RoutesLink.labor,
    label: "Labor",
    icon: Users,
    children: [
      { path: RoutesLink.labor, label: "All Labor", icon: Users },
      { path: RoutesLink.createLabor, label: "Add Labor", icon: UserPlus },
      { path: RoutesLink.bulkAddLabor, label: "Bulk Add Labor", icon: Upload },
    ],
  },
  {
    path: RoutesLink.site,
    label: "Sites",
    icon: MapPin,
    children: [
      { path: RoutesLink.site, label: "All Sites", icon: Building2 },
      { path: RoutesLink.createSite, label: "Add Site", icon: MapPin },
    ],
  },
  // {
  //   path: RoutesLink.supervisors,
  //   label: "Supervisors",
  //   icon: Users,
  //   children: [
  //     { path: RoutesLink.supervisors, label: "All Supervisors", icon: Users },
  //     { path: RoutesLink.createSupervisor, label: "Add Supervisor", icon: UserPlus },
  //   ],
  // },
  // { path: RoutesLink.department, label: "Department", icon: Building2 },
  { path: RoutesLink.shift, label: "Shift", icon: MapPin },
  {
    path: RoutesLink.wages,
    label: "Wages",
    icon: Wallet,
    children: [
      { path: RoutesLink.wageEmployees, label: "Monthly Salary Report", icon: Users },
      // { path: RoutesLink.wagePayment, label: "Wage Payments", icon: Banknote },
      { path: RoutesLink.wagePaymentHistory, label: "Payment History", icon: History },
    ],
  },
  {
    path: RoutesLink.reports,
    label: "Reports",
    icon: ClipboardList,
    children: [
      { path: RoutesLink.labourReport, label: "Labour Report", icon: Users },
      { path: RoutesLink.attendanceReport, label: "Attendance Report", icon: ClipboardCheck },
      { path: RoutesLink.wageReport, label: "Wage Report", icon: Wallet },
    ],
  },
];

const NavItemWithDropdown: React.FC<{ item: NavItem; isActive: boolean; isChildActive: boolean }> = ({
  item,
  isActive,
  isChildActive,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const Icon = item.icon;
  const hasChildren = !!item.children?.length;
  const highlighted = isActive || isChildActive;

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const close = () => setIsOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 8, left: rect.left });
    }
    setIsOpen((prev) => !prev);
  };

  if (!hasChildren) {
    return (
      <Link
        to={item.path}
        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
          highlighted
            ? "border-primary text-gray-900"
            : "border-transparent text-gray-500 hover:text-gray-700"
        }`}
      >
        <Icon size={16} />
        {item.label}
      </Link>
    );
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
          highlighted
            ? "border-primary text-gray-900"
            : "border-transparent text-gray-500 hover:text-gray-700"
        }`}
      >
        <Icon size={16} />
        {item.label}
        <ChevronDown
          size={14}
          className="text-gray-400 transition-transform duration-200"
          style={{ transform: isOpen ? "rotate(180deg)" : "none" }}
        />
      </button>

      {isOpen &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: "fixed", top: menuPos.top, left: menuPos.left }}
            className="w-64 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-[100]"
          >
            {item.children!.map((sub) => {
              const SubIcon = sub.icon;
              return (
                <Link
                  key={sub.path}
                  to={sub.path}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md transition-colors"
                >
                  <SubIcon size={16} className="text-gray-400" />
                  <span>{sub.label}</span>
                </Link>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const userDetails = useAppSelector((state: RootState) => state.user.user);

  const currentDate = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());

  const handleLogout = async () => {
    setIsLoading(true);
    const business = import.meta.env.VITE_BUSINESS;
    try {
      const deviceIdKey = business + "deviceId";
      const deviceId = localStorage.getItem(deviceIdKey) || "";
      await AuthService.logout(deviceId);
    }finally {
      deleteCookies(business + "token");
      deleteCookies(business + "refreshToken");
      localStorage.removeItem(business + "token");
      dispatch(logoutAction());
      setIsLoading(false);
      setIsLogoutModalOpen(false);
      navigate(RoutesLink.authRoutes.login);
    }
  };

  return (
    <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-gray-100 shadow-sm">
      <div className="px-4 sm:px-6 h-16 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-primary text-white flex items-center justify-center font-bold text-sm">
            S
          </div>
          <div className="leading-none">
            <p className="text-sm font-semibold text-gray-800">
              Sadguru Seva Sangh Trust
            </p>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">
              Daily Wage Register
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <span className="hidden md:block text-xs text-gray-500">
            {currentDate}
          </span>
          <button className="relative p-2 rounded-full hover:bg-gray-50 transition-colors">
            <Bell size={20} className="text-gray-600" />
            <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-red-500 border border-white"></span>
          </button>

          <div className="relative">
            <button
              onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
              className="flex items-center gap-2 p-1.5 pr-3 rounded-full hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all"
            >
              <div className="h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">
                {userDetails?.name?.charAt(0).toUpperCase() || "U"}
              </div>
              <div className="hidden sm:flex flex-col text-left">
                <span className="text-xs font-semibold text-gray-700">
                  {userDetails?.name || "User"}
                </span>
                <span className="text-[10px] text-gray-400">
                  {userDetails?.username || userDetails?.email || "—"}
                </span>
              </div>
              <ChevronDown
                size={14}
                className="text-gray-400 transition-transform duration-200"
                style={{
                  transform: isProfileDropdownOpen ? "rotate(180deg)" : "none",
                }}
              />
            </button>

            {isProfileDropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50">
                <button
                  onClick={() => {
                    setIsProfileDropdownOpen(false);
                    alert("Navigate to Profile");
                  }}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-md"
                >
                  <User size={16} />
                  <span>Profile</span>
                </button>
                <button
                  onClick={() => {
                    setIsProfileDropdownOpen(false);
                    alert("Navigate to Settings");
                  }}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-md"
                >
                  <Settings size={16} />
                  <span>Settings</span>
                </button>
                <hr className="my-1 border-gray-100" />
                <button
                  onClick={() => {
                    setIsProfileDropdownOpen(false);
                    setIsLogoutModalOpen(true);
                  }}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-md"
                >
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <nav className="flex items-center gap-1 px-4 sm:px-6 overflow-x-auto py-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const isChildActive = item.children
            ? item.children.some((c) => location.pathname.startsWith(c.path))
            : !item.children && item.path !== RoutesLink.dashboard && location.pathname.startsWith(item.path);
          return (
            <NavItemWithDropdown
              key={item.path}
              item={item}
              isActive={isActive}
              isChildActive={isChildActive}
            />
          );
        })}
      </nav>

      {/* Logout Modal using CommonModal */}
      <CommonModal
        title="Confirm Logout"
        show={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        className="max-w-sm"
      >
        <div className="flex flex-col items-center text-center">
          <div className="h-12 w-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-4">
            <LogOut size={22} />
          </div>
          <p className="text-sm text-gray-500 leading-relaxed mb-6">
            Are you sure you want to log out? You will need to log in again.
          </p>
          <div className="flex items-center gap-3 w-full">
            <button
              onClick={() => setIsLogoutModalOpen(false)}
              disabled={isLoading}
              className="flex-1 rounded-xl h-11 border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleLogout}
              disabled={isLoading}
              className="flex-1 rounded-xl h-11 bg-red-600 hover:bg-red-700 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                "Yes, Logout"
              )}
            </button>
          </div>
        </div>
      </CommonModal>
    </header>
  );
};

export default Header;